import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, CheckCircle2, ExternalLink } from "lucide-react";
import { fmtDate } from "@/lib/dates";

export const ReviewQueue = () => {
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/paperwork-review-queue").then(({ data }) => setGroups(data)).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);

  const review = async (a, decision) => {
    setBusy(a.id);
    try {
      await api.post(`/paperwork-assignments/${a.id}/review`, {
        decision, rejection_reason: decision === "Rejected" ? "Needs resubmission" : "",
      });
      toast.success(`${a.name} ${decision.toLowerCase()}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const clearToStart = async (g) => {
    setBusy(g.placement_request_id);
    try {
      await api.post(`/placement-requests/${g.placement_request_id}/clear`);
      toast.success(`${g.student_name} is cleared to start — all-clear emails sent`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (groups === null) return null;
  if (groups.length === 0) return <Card><EmptyState text="Nothing to review and nothing waiting to be cleared." /></Card>;

  return (
    <div className="space-y-4" data-testid="review-queue">
      {groups.map((g) => (
        <Card key={g.placement_request_id} className="p-0 overflow-hidden" data-testid={`queue-group-${g.placement_request_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 bg-zinc-50 border-b">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Link to={`/placements/${g.placement_request_id}`} className="font-medium text-zinc-900 hover:text-cc-blue"
                  data-testid={`queue-student-${g.placement_request_id}`}>{g.student_name}</Link>
                <ExternalLink className="w-3.5 h-3.5 text-zinc-300" />
              </div>
              <p className="text-xs text-zinc-500">
                {[g.discipline, g.facility_name, g.start_date ? `starts ${fmtDate(g.start_date)}` : null].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs" data-testid={`queue-progress-${g.placement_request_id}`}>
                {g.pending_count > 0 && <span className="text-cc-orange font-semibold mr-2">{g.pending_count} to review</span>}
                <span className="text-cc-green font-semibold">{g.approved_count}</span>
                <span className="text-zinc-400">/{g.required_count} required approved</span>
              </span>
              {g.can_clear ? (
                <Button size="sm" className="bg-cc-green hover:bg-green-600 text-white" disabled={busy === g.placement_request_id}
                  onClick={() => clearToStart(g)} data-testid={`queue-clear-${g.placement_request_id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1" />Clear to Start
                </Button>
              ) : <StatusBadge status={g.clearance_status || "Paperwork In Progress"} />}
            </div>
          </div>
          <div className="divide-y">
            {g.items.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-2.5" data-testid={`queue-item-${a.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-zinc-800">{a.name}{a.required === false ? " (optional)" : ""}</p>
                  <p className="text-xs text-zinc-400">
                    Due {fmtDate(a.due_date)}
                    {a.file_name ? ` · ${a.file_name}` : ""}
                    {a.submitted_on_behalf ? ` · uploaded by ${a.submitted_by_name || "the school"}` : ""}
                    {a.rejection_reason ? ` · ${a.rejection_reason}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.file_location && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/paperwork-assignments/${a.id}/file`} target="_blank" rel="noreferrer"
                      className="text-cc-blue text-xs flex items-center gap-1" data-testid={`queue-view-${a.id}`}>
                      <FileText className="w-3.5 h-3.5" />View
                    </a>
                  )}
                  <StatusBadge status={a.status} />
                  {["Submitted", "Pending Review"].includes(a.status) && (
                    <>
                      <Button size="sm" className="bg-cc-green hover:bg-green-600 text-white h-8" disabled={busy === a.id}
                        onClick={() => review(a, "Approved")} data-testid={`approve-${a.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="text-red-600 h-8" disabled={busy === a.id}
                        onClick={() => review(a, "Rejected")} data-testid={`reject-${a.id}`}>Reject</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
};
